import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';

import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlineOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

import { getDeviceId, getDeviceName } from '../utils/deviceId';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);


  const handleSubmit = async (e) => {
  e.preventDefault();
  setError('');
  setLoading(true);
  try {
    const deviceId = await getDeviceId();
    const deviceName = getDeviceName();
    const { data } = await api.post('/auth/login/', {
      ...form,
      device_id: deviceId,
      device_name: deviceName,
    });

    // MFA required
    if (data.mfa_required) {
      localStorage.setItem('device_name', deviceName);
      navigate('/mfa-verify', {
        state: {
        username: data.username,
        maskedEmail: data.message,
        device_id: deviceId,
        mfa_type: data.mfa_type || 'email',
      }
    });
    return;
  }

    login(data.user, { access: data.access, refresh: data.refresh });

    // Force password change
    if (data.must_change_password) {
      navigate('/first-login');
      return;
    }

    navigate(data.user.role === 'admin' ? '/admin' : '/dashboard');
  } catch (err) {
    const errData = err.response?.data;
    if (errData?.device_bound) {
      setError(`This account is already registered on "${errData.registered_device}". Contact your administrator.`);
    } else {
      setError(errData?.error || 'Invalid credentials. Please try again.');
    }
  } finally {
    setLoading(false);
  }
}

 return (
  <Box
    sx={{
      minHeight: '100vh',
      background: '#f4f6f4',
      display: 'flex',
      alignItems: { xs: 'flex-start', sm: 'center' },
      justifyContent: 'center',
      p: { xs: 0, sm: 2 },
    }}
  >
    <Box sx={{ width: '100%', maxWidth: 420 }}>

      {/* Logo card */}
      <Box
        sx={{
          background: '#1a4d2e',
          borderRadius: '12px 12px 0 0',
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.8,
        }}
      >
        <img
          src="/logo.png"
          alt="Logo"
          style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
        />

        <Typography
          sx={{
            color: 'white',
            fontWeight: 600,
            fontSize: 18,
            textAlign: 'center',
          }}
        >
          NCAOOSCE Attendance Management System
        </Typography>

      </Box>

      {/* Form card */}
      <Box
        sx={{
          background: 'white',
          borderRadius: '0 0 12px 12px',
          border: '0.5px solid #e0e8e0',
          borderTop: 'none',
          p: 3,
        }}
      >
        <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 500 }}>
          Sign in to your account
        </Typography>

        <Typography sx={{ color: '#888', fontSize: 13, mb: 3 }}>
          Enter your credentials to access the portal
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: 13, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Username"
            fullWidth
            margin="normal"
            required
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon sx={{ color: '#999', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            margin="normal"
            required
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? (
                      <VisibilityOffIcon fontSize="small" />
                    ) : (
                      <VisibilityIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={loading}
            sx={{
              mt: 2.5,
              py: 1.4,
              fontSize: 14,
              background: '#1a4d2e',
              '&:hover': { background: '#2d7a4f' },
            }}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              'Sign In'
            )}
          </Button>
        </form>

        <Typography
          sx={{
            mt: 3,
            fontSize: 11,
            color: '#bbb',
            textAlign: 'center',
          }}
        >
          Secured Official Attendance Management System
        </Typography>
      </Box>
    </Box>
  </Box>
);
}